import React from 'react';
import './budget-progress.scss';
import {
  Badge,
  Box,
  Button,
  CollectionPreferencesProps,
  Input,
  Popover,
  PropertyFilterProps,
  Select,
  SelectProps,
  SpaceBetween
} from '@amzn/awsui-components-react';
import Icon from '@amzn/awsui-components-react/polaris/icon';
import StatusIndicator, { StatusIndicatorProps } from '@amzn/awsui-components-react/polaris/status-indicator';
import ProgressBar from '@amzn/awsui-components-react/polaris/progress-bar';
import Link from '@amzn/awsui-components-react/polaris/link';
import jp from 'jsonpath';
import moment from 'moment';
import _, { isArray } from 'lodash';
import { TableProps } from '@amzn/awsui-components-react/polaris/table';
import { FilesService } from '../../services/files-service';
import { Link as LinkReactRouter } from 'react-router-dom';
import CheckboxItem from '../../common/CheckboxItem/CheckboxItem';
import CopyText from 'common/CopyText/CopyText';
import { ColumnSetting, VisibleOption } from './config-models';
import { PropertyFilterOperator } from '@amzn/awsui-collection-hooks';
import { getString } from 'common/ui-string-labels/ui-strings-utils';
import { SYSTEM_FULFILL_NAME, SYSTEM_ASSIGNEE_ACCOUNT_ID, OrderStatus, TERMINAL_STATUSES, BUDGET_WARNING_THRESHOLD, BUDGET_CRITICAL_THRESHOLD, formatStatusLabel } from 'configuration/config';
import { StampedNote } from "@amzn/tinvent-typescript-client";
import { deriveVendorFromSku } from './vendor-utils';

interface StatusInformation {
  status: StatusIndicatorProps.Type;
  message: string;
}
export interface SortingState {
  sortBy?: string;
  sortOrder?: string;
}

export const defaultPagingOptions = [
  {
    label: '10',
    value: 10
  },
  {
    label: '30',
    value: 30
  },
  {
    label: '50',
    value: 50
  }
];

export const FILE_TYPE_RESULT = "result";
export const FILE_TYPE_ERROR = "error";
export const FILE_TYPE_DOWNLOAD = "download";
export const FILE_TYPE_UPLOAD = "upload";

/**
 * Optional config for enabling row-level inline editing.
 * Pass this to buildColumnDefinitions to auto-render inputs for editable columns.
 */
export interface EditConfig {
  editingId: string | null;
  editValues: Record<string, any>;
  onChange: (fieldId: string, value: any) => void;
  idField?: string; // defaults to 'id'
}

/**
 * Options for extended cell types that require runtime callbacks.
 * When provided, buildColumnDefinitions handles these types (entity_link,
 * status_with_tooltip, percentage, validation_badges) inline rather than
 * requiring a second pass via a separate function. Fully optional -- existing
 * callers that omit this parameter get the same behavior as before.
 */
export interface BuildColumnOptions<T = any> {
  cellRenderers?: CellTypeRenderers<T>;
  /** Returns true for synthetic detail-row children (expandable rows). */
  isDetailRow?: (item: T) => boolean;
}

export function buildColumnDefinitions(
  columnSettings: ColumnSetting[],
  onlyThese?: readonly string[],
  optionsOrEdit?: BuildColumnOptions<any> | EditConfig
): TableProps.ColumnDefinition<any>[] {
  const columnDefinitions: TableProps.ColumnDefinition<any>[] = [];
  const options = optionsOrEdit && 'cellRenderers' in optionsOrEdit ? optionsOrEdit as BuildColumnOptions<any> : undefined;
  const editConfig = optionsOrEdit && 'editingId' in optionsOrEdit ? optionsOrEdit as EditConfig : undefined;
  const cellRenderers = options?.cellRenderers;
  const isDetailRow = options?.isDetailRow ?? (() => false);

  for (const column of columnSettings) {
    const showIt: boolean = checkIfShowIt(column, onlyThese);
    if (!showIt) continue;

    // ─── Extended cell types (config-driven with runtime callbacks) ───
    if (cellRenderers && column.type) {
      const extDef = buildExtendedCellDef(column, cellRenderers, isDetailRow);
      if (extDef) {
        columnDefinitions.push(extDef);
        continue;
      }
    }

    const cellFunction = (item: any): React.ReactNode => {
      // Editable column: render Input when this row is being edited
      if (column.editable && editConfig && editConfig.editingId === item[editConfig.idField ?? 'id']) {
        if (column.editType === 'select' && column.editOptions) {
          const selectOptions = column.editOptions.map((opt) =>
            typeof opt === 'string' ? { label: opt, value: opt } : opt,
          );
          const currentValue = editConfig.editValues[column.id];
          const currentOption = selectOptions.find((opt) => opt.value === currentValue);
          return (
            <div data-testid={`edit-cell-${column.id}`}>
              <Select
                selectedOption={currentOption ?? (currentValue ? { label: currentValue, value: currentValue } : null)}
                placeholder="Select an option"
                onChange={({ detail }) => editConfig.onChange(column.id, detail.selectedOption.value)}
                options={selectOptions}
                selectedAriaLabel="Selected"
                expandToViewport
              />
            </div>
          );
        }
        return (
          <Input
            data-testid={`edit-cell-${column.id}`}
            value={String(editConfig.editValues[column.id] ?? '')}
            type={(column.editType === 'number' ? 'number' : 'text') as any}
            onChange={({ detail }) => editConfig.onChange(column.id,
              column.editType === 'number' ? (detail.value === '' ? null : Number(detail.value)) : detail.value
            )}
          />
        );
      }

      // When options with isDetailRow are provided, suppress standard cells for detail rows
      if (options?.isDetailRow && isDetailRow(item)) return null;

      let value: any = null;
      let values: string[] = [];
      let id: any = null;
      if (isArray(column.selector)) {
        // Multiple values
        value = '';
        values = [];
        for (const selector of column.selector) {
          let val = jp.value(item, selector as string);
          value += ' ' + val;
          values.push(val as string);
        }
      } else {
        // Single value
        // Uses jsonpath to get the value
        value = jp.value(item, column.selector as string);
      }

      // Show emptyText placeholder when value is null/undefined/empty
      if ((value === null || value === undefined || value === '') && column.emptyText) {
        return <span style={{ color: '#5f6b7a', fontStyle: 'italic' }}>{column.emptyText}</span>;
      }

      // Set display based on field type...
      switch (column.type) {
        case 'date':
          if (value) {
            const timeAgo = moment(value as string).fromNow();
            return <span title={value}>{timeAgo}</span>;
          }
          break;
        case 'date_only':
          if (value) {
            const date = moment(value as string).format('MM/DD/YYYY');
            return <span title={value}>{date}</span>;
          }
          break;

        case 'date_utc':
          if (value) {
            const dateUtc = moment(value as string)
              .utc()
              .format('MM/DD/YYYY');
            return <span title={value}>{dateUtc}</span>;
          }
          break;

        case 'datetime_local':
          if (value) {
            const local = moment(value as string)
              .local()
              .format('MM/DD/YYYY HH:mm:ss');
            return <span title={value}>{local}</span>;
          }
          break;

        case 'assignee_alias':
          return buildAssigneeAlias(value as string);

        case 'user_info':
          return buildUserInfo(values);

        case 'status':
          return buildStatusLabel(value);

        case 'report_type':
          return buildReportTypeLabel(value);

        case 'badge_type':
          return <Badge color={value === 'PORTAL' ? 'blue' : 'grey'}>{value}</Badge>;

        case 'filestatus':
          let statusInfo = getFileStatusInformation(value);
          return <StatusIndicator type={statusInfo.status}>{statusInfo.message}</StatusIndicator>;

        case 'selectme':
          return <CheckboxItem key={value} name="orderId" id={id}></CheckboxItem>;

        case 'resultFile':
          return buildResultFileLink(values);

        case 'ordersHistoryFile':
          return buildHistoryFileLink(values);

        case 'ordersUploadFile':
          return buildUploadOrderFileLink(values);

        case 'errorFile':
          return buildErrorFileLink(values);

        case 'image_url':
          return buildImageUrl(value as string, column.props);

        case 'link':
          let url: string = '';
          if (column.linkSetting) {
            id = encodeURIComponent(jp.value(item, column.linkSetting.idSelector as string));
            url = column.linkSetting.url.replace('{id}', id);
          }
          return (
            <LinkReactRouter to={url} state={{ id, value }}>
              {value}
            </LinkReactRouter>
          );

        case 'uploadErrorsLink':
          return buildUploadFileLink(values);
        case 'uploadError':
          return buildUploadError(value);
        case 'uploadDetails':
          return (<div>{value}</div>);
        case 'noteTooltip':
          return buildNoteTooltip(value as StampedNote | undefined);
        case 'daysOpen':
          return buildDaysOpen(value as string, item.status, item.statusHistory);
        case 'actionedBy':
          return buildActionedBy(item.status, item.statusHistory);
        case 'vendor':
          return <span>{deriveVendorFromSku(value as string)}</span>;
        case 'completedDate':
          return buildCompletedDate(value);
        case 'budgetProgress':
          return buildBudgetProgress(item);
        case 'quantityProgress':
          return buildQuantityProgress(item);
        case 'currentPo':
          return buildCurrentPo(item);
        case 'needToOrder':
          return buildNeedToOrderBadge(value);
        case 'phonetool':
          return buildPhonetoolLink(value as string);
        case 'currency':
          return <span>{formatCurrency(value)}</span>;
        default:
          return <span>{value}</span>;
      }
    };

    columnDefinitions.push({
      ...column,
      cell: cellFunction,
      sortingField: column.sortable ? (column.sortingField ?? column.id) : undefined
    });
  }
  return columnDefinitions;
}

function getFileStatusInformation(status?: string): StatusInformation {
  switch (status) {
    case 'UPLOADED':
      return { status: 'in-progress', message: 'Uploading file' };
    case 'PROCESSING':
      return { status: 'in-progress', message: 'Processing file' };
    case 'NOT_PROCESSED':
      return { status: 'stopped', message: 'File not processed' };
    case 'COMPLETED':
      return { status: 'success', message: 'Success' };
    case 'COMPLETED_WITH_ERRORS':
    default:
      return { status: 'error', message: 'Error' };
  }
}

function getOrdersUploadFileName(value: string) {
  const extension = '.xlsx'
  if (!value) {
    return "";
  }
  const parts = (value as string).split(extension);
  const fullFileName = parts[0];
  const displayName = fullFileName.split("-")[0] + extension;
  return displayName;
}

function buildUploadFileLink(values: string[]) {
  // TODO: Create link to the Orders
  if (values[0] === "COMPLETED_WITH_ERRORS") {
    const fileGuid = values[1];
    const fileName = getOrdersUploadFileName(values[2]);;
    return (
      <LinkReactRouter to={`/orders/${fileGuid}`} state={{ fileGuid, fileName }} >
        {getString("uploadOrders.table.LinkToErrorsText")}
      </LinkReactRouter>
    );
  } else {
    return (<span></span>);
  }
}

function buildUploadError(value: string) {
  if (value && value !== "") {
    return (
      <Popover
        size="small"
        position="top"
        triggerType="custom"
        dismissButton={false}
        content={
          <div>
            {value}
          </div>
        }
        >
        <Link>Errors</Link>
      </Popover>);
  } else {
    return (<span></span>);
  }
}

// Status
const STATUS_MAPPING: { [key: string]: string } = {
  APPROVED: 'info',
  CANCELLED: 'error',
  COMPLETED: 'success',
  DENIED: 'error',
  FAILED: 'error',
  NEW: 'info',
  PENDING_APPROVAL: 'pending',
  PENDING_RESELLER: 'in-progress',
  PROCESSING: 'in-progress',
  SENT: 'success',
};

function buildCompletedDate(statusHistory: any): React.ReactNode {
  if (!statusHistory || !Array.isArray(statusHistory)) return <span>-</span>;
  const completed = statusHistory.find((entry: any) => entry.status === 'COMPLETED');
  if (!completed) return <span>-</span>;
  return <span>{moment(completed.on).format('MM/DD/YYYY')}</span>;
}

/**
 * Shows who completed or cancelled the order — the alias from the statusHistory
 * entry matching the order's current terminal status. Renders "-" for orders that
 * aren't COMPLETED/CANCELLED, or when the matching statusHistory entry is missing.
 */
function buildActionedBy(status: string, statusHistory: any): React.ReactNode {
  if (!TERMINAL_STATUSES.includes(status as OrderStatus) || !Array.isArray(statusHistory)) {
    return <span>-</span>;
  }
  const terminalEntry = statusHistory.find((entry: any) => entry.status === status);
  if (!terminalEntry || !terminalEntry.by) return <span>-</span>;
  return buildPhonetoolLink(terminalEntry.by);
}

export function buildNeedToOrderBadge(value: any): React.ReactNode {
  return value
    ? <Badge color="red">{getString('common.tableUtils.needToOrder.yes')}</Badge>
    : <Badge color="green">{getString('common.tableUtils.needToOrder.no')}</Badge>;
}

export function buildPhonetoolLink(alias?: string): React.ReactNode {
  if (!alias) return <span>—</span>;
  return <Link href={`https://phonetool.amazon.com/users/${alias}`} target="_blank" external>@{alias}</Link>;
}

/**
 * Formats a numeric value as a USD currency string (e.g. 1234.5 → "$1,234.50").
 * Returns an empty string for null/undefined/empty/non-numeric input.
 */
export function formatCurrency(value: unknown): string {
  if (value === null || value === undefined || value === '') return '';
  const num = typeof value === 'number' ? value : Number(value);
  if (Number.isNaN(num)) return '';
  return num.toLocaleString('en-US', { style: 'currency', currency: 'USD' });
}

function buildBudgetProgress(item: any): React.ReactNode {
  const used = item.budgetUsed ?? 0;
  const limit = item.budgetLimit ?? 1;
  const percentUsed = Math.round(used / limit * 100);

  const className = [
    'budget-progress-wrapper',
    percentUsed >= BUDGET_CRITICAL_THRESHOLD ? 'budget-progress-critical' : percentUsed >= BUDGET_WARNING_THRESHOLD ? 'budget-progress-warning' : '',
  ].filter(Boolean).join(' ');

  return (
    <div className={className}>
      <ProgressBar
        value={Math.min(percentUsed, 100)}
        additionalInfo={`${formatCurrency(used)} / ${formatCurrency(limit)} (${percentUsed}%)`}
        {...(percentUsed > 100 ? { description: getString('common.tableUtils.budgetProgress.overBudget') } : {})}
      />
    </div>
  );
}

/**
 * Received vs requested quantity for a bulk order.
 */
export function getBulkOrderQuantities(item: any): { received: number; requested: number } {
  const received = (item?.details as any)?.receivedQuantity ?? 0;
  const requested = item?.items?.[0]?.quantity ?? 0;
  return { received, requested };
}

/**
 * A bulk order can be completed only once its received quantity has reached the requested
 * quantity. Orders with no requested quantity are treated as ineligible.
 */
export function isBulkOrderEligibleForCompletion(item: any): boolean {
  const { received, requested } = getBulkOrderQuantities(item);
  return requested > 0 && received >= requested;
}

function buildQuantityProgress(item: any): React.ReactNode {
  const { received, requested } = getBulkOrderQuantities(item);
  const percent = requested > 0 ? Math.round(received / requested * 100) : 0;

  return (
    <div className="budget-progress-wrapper">
      <ProgressBar
        value={Math.min(percent, 100)}
        additionalInfo={`${received} / ${requested}`}
        {...(percent >= 100 ? { description: 'Fully received' } : {})}
      />
    </div>
  );
}


export function buildCurrentPo(item: any): React.ReactNode {
  const basePo = item.basePo ?? '';
  const consecutive = item.currentConsecutive ?? 0;
  const padded = String(consecutive).padStart(2, '0');
  return <span>{`${basePo}-${padded}`}</span>;
}

function buildDaysOpen(requestedDate: string, status: string, statusHistory?: any): React.ReactNode {
  if (!requestedDate) {
    return <span>-</span>;
  }
  let end = moment();
  if (TERMINAL_STATUSES.includes(status as OrderStatus)) {
    const terminalEntry = Array.isArray(statusHistory)
      ? statusHistory.find((entry: any) => entry.status === status)
      : undefined;
    if (!terminalEntry) {
      return <span>-</span>;
    }
    end = moment(terminalEntry.on);
  }
  const start = moment(requestedDate);
  let businessDays = 0;
  const current = start.clone();
  while (current.isBefore(end, 'day')) {
    current.add(1, 'day');
    if (current.isoWeekday() <= 5) businessDays++;
  }
  return <span>{businessDays}d</span>;
}

function buildStatusLabel(status: string): React.ReactNode {
  if (!status) return <span>-</span>;
  const statusType: StatusIndicatorProps.Type = STATUS_MAPPING[status] as StatusIndicatorProps.Type;
  return (
    <span className="status">
      <StatusIndicator type={statusType}>{formatStatusLabel(status)}</StatusIndicator>
    </span>
  );
}

// Reads the label from the same ui-strings.json keys as the filter dropdown, so they can't diverge.
function buildReportTypeLabel(reportType?: string): React.ReactNode {
  if (!reportType) return <span>-</span>;
  const label =
    reportType === 'shop'
      ? getString('resellerReportHistory.filter.shop')
      : reportType === 'bulk'
      ? getString('resellerReportHistory.filter.bulk')
      : reportType;
  return <Badge color={reportType === 'bulk' ? 'blue' : 'grey'}>{label}</Badge>;
}

// Assignee Alias
function buildAssigneeAlias(value?: string): React.ReactNode {
  if (value) {
    const displayValue = value === SYSTEM_ASSIGNEE_ACCOUNT_ID ? SYSTEM_FULFILL_NAME : value;
    return <Box variant={'p'}> {displayValue !== SYSTEM_FULFILL_NAME ? `@${displayValue}` : displayValue} </Box>;
  }

  return (
    <Box color={'text-body-secondary'} variant={'p'}>
      Unassigned
    </Box>
  );
}

// User Alias
function buildUserInfo(values: string[]): React.ReactNode {
  const userAlias = values[0] ?? "alias unavailable";
  const userEmail = values[1] ?? "email unavailable";

  return (
    <SpaceBetween size='xs'>
      <Link href={`https://phonetool.amazon.com/users/${userAlias}`} target={"_blank"}>@{userAlias}</Link>
      <CopyText textContent={userEmail} copyContent={userEmail} isHtml={false} />
    </SpaceBetween>
  );
}

// Image
interface ImageProps {
  width: string;
}
function buildImageUrl(theUrl: string, props: ImageProps): React.ReactNode {
  return <img src={theUrl} width={props.width} />;
}

function buildResultFileLink(values: string[]): React.ReactNode {
  return buildDownloadFileLink(values, FILE_TYPE_RESULT, FilesService.instance.downloadTinventFile);
}

function buildErrorFileLink(values: string[]): React.ReactNode {
  return buildDownloadFileLink(values, FILE_TYPE_ERROR, FilesService.instance.downloadTinventFile);
}

function buildHistoryFileLink(values: string[]): React.ReactNode {
  return buildDownloadFileLink(values, FILE_TYPE_DOWNLOAD, FilesService.instance.downloadOrdersHistory);
}

function buildUploadOrderFileLink(values: string[]): React.ReactNode {
  return buildDownloadFileLink(values, FILE_TYPE_UPLOAD, FilesService.instance.downloadOrdersHistory);
}

function validateDownloadParams(values: string[], downloadType: string) {
  let isValid: boolean = true;
  if (!values) {
    isValid = false;
  }

  if (values.length < 2 || values.length > 3) {
    isValid = false;
  }

  const fileId: string = values[0];
  let fileName: string = values[1];

  if (!fileId || !fileName) {
    isValid = false;
  }

  if(downloadType !== FILE_TYPE_DOWNLOAD && downloadType !== FILE_TYPE_UPLOAD){
    const status: string = values[2];

    if (!status) {
      isValid = false;
    }

    if (isValid && downloadType === FILE_TYPE_ERROR) {
      fileName = `${fileName.split('.')[0]}_errors.xlsx`;
    }
  
    if (isValid && downloadType === FILE_TYPE_ERROR && status !== 'COMPLETED_WITH_ERRORS') {
      isValid = false;
    }
  }

  if(downloadType === FILE_TYPE_UPLOAD) {
    fileName = getOrdersUploadFileName(fileName);
  }

  return { isValid, fileId, fileName };
}

function buildDownloadFileLink(
  values: string[],
  downloadType: string,
  onFollow: (fileId: string, fileName: string, downloadType: string) => void 
): React.ReactNode {

  const { isValid, fileId, fileName } = validateDownloadParams(values, downloadType);
  if (!isValid) {
    return <span></span>;
  }

  return (
    <SpaceBetween direction="horizontal" size="xs">
      <Icon name="download" variant="link" />
      <Link
        data-testid="download-file"
        href="#"
        onFollow={() => onFollow(fileId, fileName, downloadType)}
      >
        {fileName}
      </Link>
    </SpaceBetween>
  );
}

///////////////////
export function buildFilteringProperties(columnSettings: ColumnSetting[]): PropertyFilterProps.FilteringProperty[] {
  const filters: PropertyFilterProps.FilteringProperty[] = [];
  for (const column of columnSettings) {
    if (!column.operators && !column.defaultOperator) continue;
    // Use filteringKey when the selector resolves to a nested path (e.g. "confidenceScore.total")
    // so PropertyFilter accesses the leaf value rather than a parent object.
    const key = column.filteringKey ?? column.id;
    filters.push({
      key,
      propertyLabel: column.header,
      operators: column.operators ? (column.operators as PropertyFilterOperator[]) : undefined,
      defaultOperator: column.defaultOperator ? (column.defaultOperator as PropertyFilterOperator) : undefined,
      groupValuesLabel: column.header
    });
  }
  return filters;
}

/**
 * Prepares the options for the preferences, to set visible columns
 * @param columnSettings The settings from the json file
 * @returns The array of options for visible content
 */
export function buildVisibleContentOptions(
  columnSettings: ColumnSetting[]
): CollectionPreferencesProps.VisibleContentOption[] {
  const options: CollectionPreferencesProps.VisibleContentOption[] = [];
  for (const column of columnSettings) {
    if (column.visible === VisibleOption.never) continue;

    options.push({
      id: column.id,
      label: column.header,
      editable: column.visible !== VisibleOption.always
    });
  }
  return options;
}

/**
 * Returns Cloudscape Select Options from an Array of strings
 * @param array The list of strings you want as options
 * @returns The options for the Select
 */
export function buildSelectOptionsFromStrings(array: string[]) {
  const selectOptions: SelectProps.Option[] = array.map(stringValue => {
    return { label: stringValue, value: stringValue } as SelectProps.Option;
  });
  return selectOptions;
}

/**
 * Derives a Table `trackBy` value from a row using the column config's designated identity column
 * (`isIdentity: true`). Falls back to `undefined` (AWSUI's referential-identity default) when no
 * column is marked, so selection across pagination only works correctly when a column opts in.
 */
export function deriveTrackBy<T = any>(columnSettings: ColumnSetting[]): ((item: T) => string) | undefined {
  const identityColumn = columnSettings.find(column => column.isIdentity);
  if (!identityColumn) return undefined;
  return (item: T) => String(jp.value(item as any, identityColumn.selector as string));
}

function checkIfShowIt(column: ColumnSetting, onlyThese: readonly string[] | undefined): boolean {
  if (column.visible === VisibleOption.never) {
    return false;
  }
  if (column.visible === VisibleOption.always) {
    return true;
  }
  if (!onlyThese) {
    if (column.visible === VisibleOption.no) {
      // Hidden by default
      return false;
    }

    // Visible by default
    return true;
  } else {
    // Check based on selected preferences
    return onlyThese.indexOf(column.id) >= 0;
  }
}
function buildNoteTooltip(stampedNote?: StampedNote): React.ReactNode {
  return ( stampedNote &&
    <Popover
      dismissButton={false}
      position="top"
      size="small"
      triggerType="custom"
      content={
        <>
          <span>{stampedNote?.note}</span><br/>
          <small>{stampedNote?.by} {moment(stampedNote?.on?.toISOString()).fromNow()}</small>
        </>
      }
    >
      <Button iconName="status-info" variant="icon" />
    </Popover>
  );
}


// ─── Extended cell type interfaces ───────────────────────────────────────────

/**
 * Configuration for the `status_with_tooltip` cell type.
 * Pass `getIndicatorType` and `getTooltip` via CellTypeRenderers so
 * buildColumnDefinitions stays domain-agnostic.
 */
export interface StatusWithTooltipConfig {
  getIndicatorType: (status: string | null) => StatusIndicatorProps.Type;
  getTooltip?: (status: string | null) => string | null;
  nullDisplay?: string;
}

/**
 * Configuration for the `validation_badges` cell type.
 * `subKeys` declares which sub-fields of the resolved object to render as badges.
 */
export interface ValidationBadgesConfig {
  subKeys: string[];
  getBadgeColor?: (verdict: string | undefined) => 'green' | 'red' | 'grey';
  getBadgeLabel?: (verdict: string | undefined) => string;
}

/**
 * Configuration for the `entity_link` cell type.
 * Renders the primary text (resolved from the column selector) alongside an
 * icon-only external link when `linkField` resolves to a truthy value.
 */
export interface EntityLinkConfig<T = any> {
  /** Jsonpath to the field that gates the link (e.g. "$.importResult.taxonomyId"). */
  linkField: string;
  /** Builds the href from the resolved linkField value. */
  buildUrl: (linkFieldValue: string) => string;
  /** Optional aria-label for the link. */
  ariaLabel?: string;
  /** Called when the link is followed. Receives the full row item. */
  onFollow?: (item: T) => void;
  /** Optional renderer for detail-row children (expandable rows pattern). */
  detailRowRenderer?: (item: any) => React.ReactNode;
}

/**
 * Runtime cell-type renderers that table consumers pass into buildColumnDefinitions
 * to handle domain-specific cell types declared in JSON config.
 */
export interface CellTypeRenderers<T = any> {
  status_with_tooltip?: StatusWithTooltipConfig;
  percentage?: { selector?: string };
  validation_badges?: ValidationBadgesConfig;
  entity_link?: EntityLinkConfig<T>;
}

// ─── Default badge helpers ───────────────────────────────────────────────────

export function getValidationBadgeColor(verdict: string | undefined): 'green' | 'red' | 'grey' {
  if (!verdict) return 'grey';
  switch (verdict.toUpperCase()) {
    case 'PASS': return 'green';
    case 'FAIL': return 'red';
    default: return 'grey';
  }
}

export function getValidationBadgeLabel(verdict: string | undefined): string {
  if (!verdict) return getString('contentBackfill.verdict.unknown') || 'Unknown';
  switch (verdict.toUpperCase()) {
    case 'PASS': return getString('contentBackfill.verdict.pass') || 'Pass';
    case 'FAIL': return getString('contentBackfill.verdict.fail') || 'Fail';
    default: return verdict;
  }
}

/** Maps validation subKey IDs to their display labels for the collapsed badge column. */
export const VALIDATION_SUBKEY_LABELS: Record<string, string> = {
  membership: getString('contentBackfill.validation.membershipLabel') || 'Membership',
  accuracy: getString('contentBackfill.validation.accuracyLabel') || 'Accuracy',
  classification: getString('contentBackfill.validation.classificationLabel') || 'Classification',
  style: getString('contentBackfill.validation.styleLabel') || 'Style'
};

// ─── Extended buildColumnDefinitions overload ────────────────────────────────

/**
 * Builds a single column definition for extended cell types that require
 * runtime callbacks (status_with_tooltip, percentage, validation_badges,
 * entity_link). Returns null if the column type is not an extended type,
 * letting the caller fall through to the standard switch.
 *
 * This is a private helper used exclusively by buildColumnDefinitions when
 * the optional `cellRenderers` parameter is provided.
 */
function buildExtendedCellDef<T = any>(
  setting: ColumnSetting,
  cellRenderers: CellTypeRenderers<T>,
  isDetailRow: (item: T) => boolean
): TableProps.ColumnDefinition<T> | null {
  switch (setting.type) {
    case 'status_with_tooltip': {
      const config = cellRenderers.status_with_tooltip;
      if (!config) return null;
      return {
        ...setting,
        cell: (item: T) => {
          if (isDetailRow(item)) return null;
          const value = jp.value(item as any, setting.selector as string);
          if (config.nullDisplay !== undefined && value === null) {
            return <span>{config.nullDisplay}</span>;
          }
          if (!value) return <span>-</span>;
          const tooltip = config.getTooltip?.(value);
          const indicator = (
            <StatusIndicator type={config.getIndicatorType(value)}>
              {value}
            </StatusIndicator>
          );
          if (!tooltip) return indicator;
          return (
            <span title={tooltip} data-testid={`status-tooltip-${String(value).toLowerCase()}`}>
              {indicator}
            </span>
          );
        },
        sortingComparator: (a: T, b: T) => {
          const aVal = String(jp.value(a as any, setting.selector as string) ?? '');
          const bVal = String(jp.value(b as any, setting.selector as string) ?? '');
          return aVal.localeCompare(bVal);
        },
        sortingField: setting.sortable ? (setting.sortingField ?? setting.id) : undefined
      };
    }

    case 'percentage': {
      return {
        ...setting,
        cell: (item: T) => {
          if (isDetailRow(item)) return null;
          const value = jp.value(item as any, setting.selector as string);
          if (value === null || value === undefined) return '-';
          return `${Number(value).toFixed(0)}%`;
        },
        sortingComparator: (a: T, b: T) => {
          const aVal = jp.value(a as any, setting.selector as string) ?? -1;
          const bVal = jp.value(b as any, setting.selector as string) ?? -1;
          return Number(aVal) - Number(bVal);
        },
        sortingField: setting.sortable ? (setting.sortingField ?? setting.id) : undefined
      };
    }

    case 'validation_badges': {
      const config = cellRenderers.validation_badges;
      if (!config) return null;
      const subKeys: string[] = (setting.props?.subKeys as string[] | undefined) ?? config.subKeys;
      const getBadgeColor = config.getBadgeColor ?? getValidationBadgeColor;
      const getBadgeLabel = config.getBadgeLabel ?? getValidationBadgeLabel;
      return {
        ...setting,
        cell: (item: T) => {
          if (isDetailRow(item)) return null;
          const parentObj = jp.value(item as any, setting.selector as string);
          if (!parentObj) return '-';
          const badges: React.ReactNode[] = [];
          for (const subKey of subKeys) {
            const sub = parentObj[subKey];
            if (!sub) continue;
            const verdict = sub.verdict as string | undefined;
            const label = VALIDATION_SUBKEY_LABELS[subKey] || subKey;
            badges.push(
              <SpaceBetween key={subKey} direction="horizontal" size="xxs">
                <Box variant="small" color="text-body-secondary">{label}:</Box>
                <Badge color={getBadgeColor(verdict)}>
                  {getBadgeLabel(verdict)}
                </Badge>
              </SpaceBetween>
            );
          }
          if (badges.length === 0) return '-';
          return (
            <SpaceBetween direction="vertical" size="xxs">
              {badges}
            </SpaceBetween>
          );
        },
        sortingField: setting.sortable ? (setting.sortingField ?? setting.id) : undefined
      };
    }

    case 'entity_link': {
      const config = cellRenderers.entity_link;
      if (!config) return null;
      const linkField = (setting.props?.linkField as string | undefined) ?? config.linkField;
      return {
        ...setting,
        cell: (item: T) => {
          if (isDetailRow(item)) {
            return config.detailRowRenderer ? config.detailRowRenderer(item) : null;
          }
          const displayValue = jp.value(item as any, setting.selector as string);
          const linkValue = linkField ? jp.value(item as any, linkField) : null;
          const testId = (item as any).sourceSourceId ?? displayValue;
          return (
            <SpaceBetween direction="horizontal" size="xs">
              <span data-testid={`item-name-${testId}`}>{displayValue || '-'}</span>
              {linkValue && (
                <Link
                  href={config.buildUrl(linkValue)}
                  external
                  ariaLabel={config.ariaLabel}
                  variant="primary"
                  onFollow={() => {
                    config.onFollow?.(item);
                  }}
                />
              )}
            </SpaceBetween>
          );
        },
        sortingComparator: (a: T, b: T) => {
          const aVal = String(jp.value(a as any, setting.selector as string) ?? '');
          const bVal = String(jp.value(b as any, setting.selector as string) ?? '');
          return aVal.localeCompare(bVal);
        },
        sortingField: setting.sortable ? (setting.sortingField ?? setting.id) : undefined
      };
    }

    default:
      return null;
  }
}

/**
 * @deprecated Use buildColumnDefinitions with the `options.cellRenderers` parameter instead.
 * Kept temporarily for backwards compatibility during migration.
 */
export function applyExtendedCellTypes<T = any>(
  columnDefinitions: TableProps.ColumnDefinition<T>[],
  columnSettings: ColumnSetting[],
  cellRenderers: CellTypeRenderers<T>,
  opts?: { isDetailRow?: (item: T) => boolean }
): TableProps.ColumnDefinition<T>[] {
  const isDetailRow = opts?.isDetailRow ?? (() => false);

  return columnDefinitions.map((colDef) => {
    const setting = columnSettings.find((s) => s.id === colDef.id);
    if (!setting?.type) {
      if (opts?.isDetailRow) {
        const originalCell = colDef.cell;
        return {
          ...colDef,
          cell: (item: T) => {
            if (isDetailRow(item)) return null;
            return originalCell ? originalCell(item) : null;
          }
        };
      }
      return colDef;
    }

    const extDef = buildExtendedCellDef(setting, cellRenderers, isDetailRow);
    if (extDef) return { ...colDef, ...extDef };
    return colDef;
  });
}

// ─── Collection Preferences helper ──────────────────────────────────────────

export interface CollectionPreferencesConfig {
  pageSizeOptions?: typeof defaultPagingOptions;
  visibleContentOptions: CollectionPreferencesProps.VisibleContentOption[];
}

/**
 * Builds standardised CollectionPreferences props (page size, visible content, striped rows)
 * from column settings, removing inline JSX boilerplate from table components.
 */
export function buildCollectionPreferencesProps(
  columnSettings: ColumnSetting[],
  overrides?: { pageSizeOptions?: typeof defaultPagingOptions }
): CollectionPreferencesConfig {
  return {
    pageSizeOptions: overrides?.pageSizeOptions ?? defaultPagingOptions,
    visibleContentOptions: buildVisibleContentOptions(columnSettings)
  };
}

